import styled from '@emotion/styled';
import { t } from '@lingui/macro';
import {
  Dialog as MuiModal,
  Box,
  dialogClasses,
  DialogActions,
  DialogTitle,
  DialogContent as MuiDialogContent,
  useTheme,
} from '@mui/material';
import { Minus, X } from '@phosphor-icons/react';

import DelogueLogoBig from 'img/delogue-logo-big.svg';

import { ModalContentProps, ModalLayoutProps, ModalProps, Action } from '../types';
import { Image } from 'components/image/Image';
import { IconButton } from 'components/button/IconButton';
import { Checkbox } from 'components/inputs/Checkbox';
import { Text, type TextProps } from 'components/text/Text';
import { Button, type ButtonProps } from 'components/button/Button';

const getContent = (value: string | ModalContentProps): ModalContentProps => {
  return typeof value === 'string' ? { description: value } : value;
};

/**
 * Modal component, part of Delogue design system.
 * @param title - title text displayed in the modal header
 * @param description - description text displayed below the title
 * @param icon - optional icon element displayed in the header
 * @param hideCloseIcon - when true, hides the close (X) button in the top-right corner
 * @param contents - content object(s) with title and description, can be a single object or array
 * @param children - child elements to render in the modal content area
 * @param headerProps - additional props to pass to the DialogTitle component
 * @param titleRootProps - additional props to pass to the title root Box component
 * @param descriptionProps - additional props to pass to the description Text component
 * @param titleProps - additional props to pass to the title Text component
 * @param closeIconProps - additional props to pass to the close IconButton component
 * @param actions - array of action button configurations (id, label, onClick, etc.)
 * @param modalContentRootProps - additional props to pass to the DialogContent component
 * @param modalModalActionProps - additional props to pass to the DialogActions component
 * @param dontShowAgain - whether the "Don't show again" checkbox is checked, default is false
 * @param onDontShowAgainChange - callback function when the "Don't show again" checkbox changes
 * @param hideDontShowAgain - when true, hides the "Don't show again" checkbox
 * @param variant - modal layout variant: 'default', 'centerAligned', 'leftAligned', or 'horizontalAligned'
 * @param onClose - callback function when the modal is closed (via close button, escape key, etc.)
 * @param onActionsClick - callback function when any action button is clicked
 * @param id - unique identifier for the modal, used as form ID
 * @param handleMinimize - callback function when the minimize button is clicked
 * @param minimize - when true, shows the minimize button in the top-right corner
 * @param modalSx - Optional overrides: `window` (Paper), `windowBody` (DialogContent), `windowActions` (DialogActions). Defaults use `theme.s.lg` (24px design spacing); your `sx` is merged **after** defaults so you only pass what should differ.
 * @param corner - when true, positions the modal in the bottom-right corner with transparent backdrop
 * @param modalColor - color theme for the modal: 'primary', 'secondary', or 'tertiary'
 * @param image - optional image source to display in the modal
 */
export const Modal = ({
  title,
  description,
  icon,
  hideCloseIcon,
  contents,
  headerProps,
  titleRootProps,
  descriptionProps,
  titleProps,
  closeIconProps,
  actions: actionsProps,
  modalContentRootProps,
  modalModalActionProps,
  dontShowAgain = false,
  onDontShowAgainChange,
  hideDontShowAgain,
  hideActions,
  variant = 'default',
  children,
  onClose,
  onActionsClick,
  id,
  handleMinimize,
  minimize,
  modalSx,
  corner,
  modalColor,
  image,
  slotProps,
  'data-testid': dataTestId,
  ...rest
}: ModalProps) => {
  const onkeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      onClose?.(e, 'escape');
    }
  };
  // `slotProps.paper` may be an object or a callback; only read `sx` from the object form.
  const callerPaperSx =
    slotProps?.paper && typeof slotProps.paper === 'object' ? slotProps.paper.sx : undefined;
  return (
    <StyledModal
      corner={corner}
      modalColor={modalColor}
      hasImage={Boolean(image)}
      slotProps={{
        ...slotProps,
        paper: {
          ...slotProps?.paper,
          // Merge the caller's paper sx with the modal's window styles so neither is lost
          // (modalSx.window is last so the modal's structural styles win on conflict).
          sx: [
            ...(Array.isArray(callerPaperSx) ? callerPaperSx : [callerPaperSx]),
            modalSx?.window,
          ].filter(Boolean),
        },
      }}
      id={id}
      maxWidth="lg"
      data-testid={dataTestId || 'modal'}
      {...rest}
      hideBackdrop={corner}
      onKeyDown={onkeyDown}
    >
      <ModalLayout
        title={title}
        description={description}
        icon={icon}
        hideCloseIcon={hideCloseIcon}
        contents={contents}
        headerProps={headerProps}
        titleRootProps={titleRootProps}
        descriptionProps={descriptionProps}
        titleProps={titleProps}
        closeIconProps={closeIconProps}
        actions={actionsProps}
        modalContentRootProps={modalContentRootProps}
        modalModalActionProps={modalModalActionProps}
        dontShowAgain={dontShowAgain}
        onDontShowAgainChange={onDontShowAgainChange}
        hideDontShowAgain={hideDontShowAgain}
        hideActions={hideActions}
        variant={variant}
        onClose={onClose}
        onActionsClick={onActionsClick}
        id={id}
        handleMinimize={handleMinimize}
        minimize={minimize}
        modalSx={modalSx}
        image={image}
        modalColor={modalColor}
        corner={corner}
      >
        {children}
      </ModalLayout>
    </StyledModal>
  );
};

const ModalLayout = ({
  title,
  description,
  icon,
  hideCloseIcon,
  contents,
  headerProps,
  titleRootProps,
  descriptionProps,
  titleProps,
  closeIconProps,
  actions: actionsProps,
  modalContentRootProps,
  modalModalActionProps,
  dontShowAgain = false,
  onDontShowAgainChange,
  hideDontShowAgain,
  hideActions,
  variant = 'default',
  children,
  onClose,
  onActionsClick,
  id: formID,
  handleMinimize,
  minimize,
  modalSx,
  image,
  modalColor,
  corner,
}: ModalLayoutProps) => {
  const theme = useTheme();

  const actions = actionsProps?.filter(action => action?.id !== 'cancel' && action?.id !== 'apply');

  const actionCancel = {
    variant: 'secondary',
    size: 'lg',
    ...(actionsProps?.find(action => action?.id === 'cancel') ?? {}),
  } as Action;

  const actionApply = {
    variant: 'primary',
    size: 'lg',
    ...(actionsProps?.find(action => action?.id === 'apply') ?? {}),
  } as Action;

  const descriptionInBody = Boolean(description && !contents && !children);

  return (
    <>
      {image ? (
        <ImageCon>
          <Image
            src={DelogueLogoBig}
            alt="Delogue logo name"
            width="100%"
            height="100%"
            fit="contain"
          />
          <Image src={image} alt="Modal image" width="100%" height="100%" fit="contain" />
        </ImageCon>
      ) : null}
      <ModalContainer className="modal-layout-container">
        <ModalHeader
          {...headerProps}
          sx={[
            {
              alignItems: variant === 'centerAligned' ? 'center' : 'flex-start',
              flexDirection: variant === 'horizontalAligned' ? 'row' : 'column',
            },
            ...(Array.isArray(headerProps?.sx) ? headerProps.sx : [headerProps?.sx]),
          ]}
        >
          {variant !== 'default' && icon}
          {(title || description || variant === 'default' || variant === 'centerAligned') && (
            <Header
              {...titleRootProps}
              sx={[
                {
                  gap: variant === 'default' && icon ? '16px' : '4px',
                  alignItems: variant === 'centerAligned' ? 'center' : 'flex-start',
                  flexDirection: 'column',
                },
                ...(Array.isArray(titleRootProps?.sx) ? titleRootProps.sx : [titleRootProps?.sx]),
              ]}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                }}
              >
                {variant === 'default' && icon}
                {title && <ModalTitle {...titleProps}>{title}</ModalTitle>}
              </Box>
              {description && !descriptionInBody && (
                <ModalDescription
                  {...descriptionProps}
                  {...(variant === 'default' && !icon ? { variant: 'bodySm' } : {})}
                >
                  {description}
                </ModalDescription>
              )}
            </Header>
          )}
          <ModalControls className="modal-Controls">
            {minimize && (
              <IconButton
                data-testid="minimize_modal"
                size="lg"
                onClick={handleMinimize}
                title={t`Minimize`}
              >
                <Minus
                  color={theme.palette.border.primary}
                  size={24}
                  style={{ minWidth: '24px' }}
                />
              </IconButton>
            )}
            {!hideCloseIcon && (
              <IconButton
                data-testid="close_modal"
                size="lg"
                {...closeIconProps}
                onClick={e => {
                  onClose?.(e, 'closeClick');
                }}
              >
                <X
                  color={theme.palette.border.primary}
                  size={corner ? 32 : 24}
                  style={{ minWidth: corner ? '32px' : '24px' }}
                />
              </IconButton>
            )}
          </ModalControls>
        </ModalHeader>
        {(contents || children || descriptionInBody) && (
          <DialogContent
            {...modalContentRootProps}
            sx={{
              padding: `0 24px`,
              paddingTop: '16px',
              ...modalSx?.windowBody,
            }}
          >
            {descriptionInBody && (
              <ModalDescription
                {...descriptionProps}
                {...(variant === 'default' && !icon ? { variant: 'bodySm' } : {})}
              >
                {description}
              </ModalDescription>
            )}
            {contents &&
              (Array.isArray(contents) ? (
                contents?.map((item, i) => (
                  <ModalContent key={`contents-${i + 1}`} {...getContent(item)} />
                ))
              ) : (
                <ModalContent {...getContent(contents)} />
              ))}
            {children}
          </DialogContent>
        )}
        {!hideActions && (
          <ModalActions
            {...modalModalActionProps}
            sx={{
              padding: 24,
              gap: '12px',
              justifyContent: hideDontShowAgain ? 'flex-end' : '',
              ...modalSx?.windowActions,
            }}
          >
            {!hideDontShowAgain && (
              <CheckboxCon>
                <Checkbox
                  checked={dontShowAgain}
                  onChange={onDontShowAgainChange}
                  label={
                    <Text
                      variant="bodyXs"
                      weight="regular"
                      color={modalColor === 'primary' ? 'primaryOnBrand' : undefined}
                    >
                      {t`Don't show again`}
                    </Text>
                  }
                />
              </CheckboxCon>
            )}
            {actions?.map(({ id, ...action }) => (
              <Button
                key={id}
                size="lg"
                {...action}
                onClick={e => {
                  onActionsClick?.(e, id);
                  return action?.onClick?.(e, id);
                }}
                disabled={action.disabled || false}
              >
                {action?.label}
              </Button>
            ))}
            {!actionCancel?.hideAction && actionCancel.id ? (
              <Button
                {...(actionCancel as ButtonProps)}
                onClick={e => {
                  actionCancel?.onClick?.(e, 'cancel');
                  onActionsClick?.(e, 'cancel');
                  return onClose?.(e, 'cancel');
                }}
                disabled={actionCancel.disabled || false}
              >
                {actionCancel?.label ?? t`Cancel`}
              </Button>
            ) : null}
            {!actionApply?.hideAction && actionApply.id ? (
              <Button
                {...(actionApply as ButtonProps)}
                onClick={e => {
                  onActionsClick?.(e, 'apply');
                  return actionApply?.onClick?.(e, 'apply');
                }}
                disabled={actionApply.disabled || false}
              >
                {actionApply?.label ?? t`Confirm`}
              </Button>
            ) : null}
          </ModalActions>
        )}
      </ModalContainer>
    </>
  );
};

const ModalActions = styled(DialogActions)`
  display: flex;
  flex-shrink: 0;
  margin-top: auto;
  & > :not(style) ~ :not(style) {
    margin-left: 0;
  }
`;

export const CheckboxCon = styled.div`
  flex: 1;
  white-space: nowrap;
  .MuiCheckbox-root {
    padding-left: 0;
  }
`;

const ModalTitle = (props: TextProps) => {
  return <Text weight="semiBold" variant="headlineXs" {...props} />;
};

const ModalDescription = (props: TextProps) => {
  return (
    <Text
      weight="regular"
      variant="bodyMd"
      color="secondary"
      {...props}
      style={{ width: '100%' }}
    />
  );
};

const ModalHeader = styled(DialogTitle)`
  display: flex;
  gap: 16px;
  padding: 24px;
  padding-bottom: 0px;
  flex-shrink: 0;
`;
// As Mark suggested, the gap between the header and the contact section should be 16px for all type of modal and footer have 24px padding from all side so contact section bottom gap is 0px
const DialogContent = styled(MuiDialogContent)`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
`;

const Column = styled(Box)`
  display: flex;
  flex-direction: column;
`;

const Header = styled(Box)`
  display: flex;
  width: 100%;
`;

const Row = styled(Box)`
  display: flex;
  flex-direction: row;
`;

const ModalControls = styled(Box)`
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;
const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
`;

const ImageCon = styled.div`
  width: 280px;
  height: 254px;
  position: relative;
  margin: ${p => p.theme.s.lg};
  > img {
    position: absolute;
  }
`;

export const ModalContent = ({
  description,
  titleIcon,
  title,
  startIcon,
  gap = '8px',
  titleProps,
}: ModalContentProps) => {
  return (
    <Row sx={{ gap }}>
      {startIcon}
      <Column sx={{ gap }}>
        <Row sx={{ gap, alignItems: 'center' }}>
          {titleIcon}
          <ModalTitle {...titleProps}>{title}</ModalTitle>
        </Row>
        <ModalDescription weight="regular">{description}</ModalDescription>
      </Column>
    </Row>
  );
};

const StyledModal = styled(MuiModal)<{
  corner?: boolean;
  modalColor?: 'primary' | 'secondary' | 'tertiary';
  hasImage?: boolean;
}>`
  background: rgba(0, 0, 0, 0.2);
  ${p => p.corner && 'pointer-events: none;'}
  ${p => (p.corner ? 'background: transparent;' : '')}
  .${dialogClasses.paper} {
    ${p => p.corner && 'pointer-events: auto;'}
    /* MUI sets overflowY: auto on the paper, which scrolls the whole dialog. Hide overflow here so only DialogContent scrolls. */
    overflow: hidden;
    min-width: 560px;
    border-radius: 8px;
    border: 0.5px solid ${p => p.theme.palette.border.primary};
    background: ${p => p.theme.palette.bg.primary};
    box-shadow: 0px 0px 4px 0px rgba(34, 34, 34, 0.08);
    ${p => (p.corner ? 'position: absolute; bottom: 0; right: 0;' : '')}
    ${p => (p.modalColor === 'primary' ? `background: ${p.theme.palette.fg.brandPrimary};` : '')}
    ${p => (p.modalColor === 'primary' ? `border-color: ${p.theme.palette.border.brand};` : '')}
    ${p => (p.hasImage ? 'display: flex; flex-direction: row; align-items: center;' : '')}
    ${p =>
      p.modalColor === 'primary'
        ? `
      h2
      * {
        color: ${p.theme.palette.text.primaryOnBrand};
      }
      svg, path, circle, rect, line, polyline, polygon {
        color: ${p.theme.palette.text.primaryOnBrand} !important;
        fill: ${p.theme.palette.text.primaryOnBrand} !important;
        stroke: ${p.theme.palette.text.primaryOnBrand} !important;
      }
    `
        : ''}
  }
`;
